import { mediaIdImageField } from '@/lib/mediaImageField';
import { GreaterAreaDropdown, ThemeTypeDropdown, TourTypeDropdown } from '@/components/dropdowns';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import { TourStatusBadge, TourThemeInlineEdit } from '@/components/tours';
import { TourImageCell } from '@/components/tours/TourImageCell';
import { Badge, type CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';

// Define type based on our Prisma schema
export interface Tour {
  id: string;
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  description?: string;
  voiceoverId?: string;
  tagline: string;
  featuredImageId?: string;
  tourTypeId: string;
  defaultThemeTypeId?: string;
  greaterAreaId?: string;
  duration?: string;
  stops?: number;
  difficulty?: 'Easy' | 'Moderate' | 'Challenging';
  highlights?: string[];
  estimatedWalking?: string;
  pathMode?: 'defined' | 'fastest';
  /**
   * Set when a mobile-app user created this tour themselves. User-generated
   * tours are protected from internal modification — the API rejects stop
   * changes with 403 — so the portal renders them read-only.
   */
  ownerId?: string | null;
  createdAt: string;
  updatedAt: string;
  conclusion?: string;
  // Sync status fields (computed by API)
  isDescriptionVoiceoverInSync?: boolean | null;
  isConclusionVoiceoverInSync?: boolean | null;
  // Relations for display
  tourType?: {
    id: string;
    name: string;
  };
  defaultThemeType?: {
    id: string;
    name: string;
    slug: string;
  };
  greaterArea?: {
    id: string;
    name: string;
  };
  featuredImage?: {
    id: string;
    filename: string;
    name: string;
    mimeType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    filename: string;
    name: string;
    mimeType: string;
    url?: string;
  };
  voiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  conclusionVoiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  // Attractions array (included when fetching with language parameter for themed content)
  attractions?: Array<{
    id: string;
    order: number;
    attractionThemeId?: string | null;
    attraction: {
      id: string;
      name: string;
      description?: string;
      longDescription?: string;
      featuredImage?: { id: string; url?: string } | null;
      voiceover?: { id: string; url?: string; fileType?: string } | null;
    };
  }>;
}

/**
 * Tour CRUD configuration
 */
export const tourCrudConfig: CrudConfig<Tour> = {
  entityName: 'Tour',
  entityNamePlural: 'Tours',

  endpoints: {
    list: '/tours',
    single: (id: string) => `/tours/${id}`,
    create: '/tours',
    update: (id: string) => `/tours/${id}`,
    delete: (id: string) => `/tours/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter tour name',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'published',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Needs Review', value: 'needs_review' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <TourStatusBadge status={value as Tour['status']} />;
      },
    },
    {
      key: 'tagline',
      label: 'Tagline',
      type: 'text',
      required: false,
      placeholder: 'Enter compelling tagline',
      validation: {
        min: 5,
        max: 100,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter tour description',
      helpText: 'Use the toolbar to format your text',
    },
    {
      key: 'conclusion',
      label: 'Conclusion',
      type: 'richtext',
      required: false,
      placeholder: 'Enter tour conclusion',
      helpText: 'Closing text shown at the end of the tour',
    },
    {
      key: 'conclusionVoiceover',
      label: 'Conclusion Voiceover',
      type: 'custom',
      required: false,
      format: (value: Tour['conclusionVoiceover'], fullRecord?: Tour) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isConclusionVoiceoverInSync ?? null}
            outOfSyncMessage="Conclusion has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'voiceoverId',
      label: 'Description Voiceover',
      type: 'audio',
      required: false,
      helpText: 'Audio media ID for description voiceover',
    },
    {
      key: 'voiceover',
      label: 'Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: Tour['voiceover'], fullRecord?: Tour) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isDescriptionVoiceoverInSync ?? null}
            outOfSyncMessage="Description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      ...mediaIdImageField,
      required: false,
      helpText: 'Upload tour featured image (max 5MB)',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: Tour['featuredImage'], fullRecord?: Tour) => {
        return <TourImageCell tour={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: Tour['featuredImage'], fullRecord?: Tour) => {
        return <TourImageCell tour={fullRecord} size="lg" />;
      },
    },
    {
      key: 'tourTypeId',
      label: 'Tour Type',
      type: 'custom',
      required: true,
      placeholder: 'Select Tour Type',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => <TourTypeDropdown value={value} onChange={onChange} placeholder={field.placeholder} />,
    },
    {
      key: 'tourType',
      label: 'Tour Type',
      type: 'text',
      required: false,
      placeholder: 'Tour Type',
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/tour-types/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'greaterAreaId',
      label: 'Greater Area',
      type: 'custom',
      required: false,
      placeholder: 'Select Greater Area',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <GreaterAreaDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
    },
    {
      key: 'greaterArea',
      label: 'Greater Area',
      type: 'text',
      required: false,
      placeholder: 'Greater Area',
      disableRowClick: true,
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/greater-areas/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'defaultThemeTypeId',
      label: 'Default Theme Type',
      type: 'custom',
      required: false,
      placeholder: 'Select Theme Type',
      helpText: 'Choose a theme for this tour (e.g., "Ghostly" for ghost tours). Attractions will use this theme when available.',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => <ThemeTypeDropdown value={value} onChange={onChange} placeholder={field.placeholder} />,
    },
    {
      key: 'defaultThemeType',
      label: 'Theme',
      type: 'custom',
      required: false,
      placeholder: 'No theme set',
      disableRowClick: true,
      format: (_value: { id: string; name: string; slug: string } | undefined, fullRecord?: Tour) => {
        return <TourThemeInlineEdit tour={fullRecord} />;
      },
    },
    {
      key: 'duration',
      label: 'Duration',
      type: 'text',
      required: false,
      placeholder: 'e.g., 1-2 hours',
      validation: {
        max: 100,
      },
    },
    {
      key: 'stops',
      label: 'Stops',
      type: 'number',
      required: false,
      placeholder: 'Number of stops',
      validation: {
        min: 0,
      },
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      type: 'select',
      required: false,
      defaultValue: 'Easy',
      options: [
        { label: 'Easy', value: 'Easy' },
        { label: 'Moderate', value: 'Moderate' },
        { label: 'Challenging', value: 'Challenging' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <Badge variant="default">{value}</Badge>;
      },
    },
    {
      key: 'estimatedWalking',
      label: 'Estimated Walking',
      type: 'text',
      required: false,
      placeholder: 'e.g., 0 miles',
      validation: {
        max: 100,
      },
    },
    {
      key: 'pathMode',
      label: 'Route Mode',
      type: 'select',
      required: false,
      defaultValue: 'fastest',
      options: [
        { label: 'Fastest (location-based)', value: 'fastest' },
        { label: 'Defined route (custom order)', value: 'defined' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return (
          <Badge variant="default">
            {value === 'defined' ? 'Defined route' : 'Fastest'}
          </Badge>
        );
      },
    },
    {
      key: 'highlights',
      label: 'Highlights',
      type: 'textarea',
      required: false,
      placeholder: 'Enter highlights separated by new lines',
      validation: {
        max: 1000,
      },
      format: (value: string[] | undefined) => {
        if (!value || value.length === 0)
          return <span className="text-muted-foreground">No highlights</span>;
        return (
          <div className="space-y-1">
            {value.map((highlight, index) => (
              <Badge key={index} variant="" className="mx-1">
                {highlight}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['featuredImage', 'name', 'status', 'greaterArea', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'tagline'],
    sortableFields: ['name', 'status', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'multiselect',
        options: [
          { label: 'Published', value: 'published' },
          { label: 'Draft', value: 'draft' },
          { label: 'Needs Review', value: 'needs_review' },
        ],
      },
    ],
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core tour details',
        fields: ['featuredImageLarge', 'status', 'tagline', 'voiceover', 'description', 'conclusionVoiceover', 'conclusion'],
        defaultExpanded: true,
      },
      {
        title: 'Tour Details',
        description: 'Duration, difficulty and logistics',
        fields: ['duration', 'stops', 'difficulty', 'estimatedWalking', 'pathMode', 'highlights'],
        defaultExpanded: true,
      },
      {
        title: 'Classification',
        description: 'Tour type and location details',
        fields: ['tourType', 'greaterArea'],
        defaultExpanded: true,
      },
      {
        title: 'Theme Settings',
        description: 'Theme configuration for this tour',
        fields: ['defaultThemeType'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Tour',
        fields: [
          'name',
          'status',
          'tourTypeId',
          'greaterAreaId',
          'featuredImageId',
          'tagline',
          'description',
          'conclusion',
        ],
        columns: 1,
      },
      {
        title: 'Theme Settings',
        description: 'Theme configuration for this tour',
        fields: ['defaultThemeTypeId'],
        columns: 1,
      },
      {
        title: 'Route',
        description:
          'Pick how mobile users walk this tour. Fastest picks the closest unvisited stop based on their location. Defined route follows the order set on the Attractions tab.',
        fields: ['pathMode'],
        columns: 1,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 2,
        max: 200,
      },
      status: {
        required: true,
      },
      tagline: {
        required: true,
        min: 5,
        max: 100,
      },
      featuredImageId: {
        required: false,
      },
      tourTypeId: {
        required: true,
      },
      difficulty: {
        required: false,
      },
      stops: {
        min: 0,
      },
      duration: {
        max: 100,
      },
      estimatedWalking: {
        max: 100,
      },
      highlights: {
        max: 1000,
      },
    },
  },
};
